import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useLoaderData, useNavigate } from '@remix-run/react';
import {
  IntegrationsManager,
  Player as PlayerWithUI,
} from '@brightcove/web-sdk/ui';

import { getBoolValue, isLive } from '~/components/player/utils';
import { getVideoType } from './utils';
import logger from '~/services/logger';
import { PlayNext } from '~/components/player/play-next';
import usePlayerAnalytics from '../hooks/use-player-analytics';
import usePlayerTopBar from '../hooks/use-player-top-bar';
import usePlayNext from '../hooks/use-play-next';

import type { Player } from '@brightcove/web-sdk/ui';
import type { LoaderData } from '~/components/player/index.client';
import type { PlayerTrackData } from '~/components/player/tracking.ts';
import type { Feed } from '~/services/layout';

declare global {
  interface Window {
    // BrightcoveWebSdkSsaiIntegration is available after loading the index.iife.min.js script
    BrightcoveWebSdkSsaiIntegration: any;
  }
}

if (window.BrightcoveWebSdkSsaiIntegration) {
  IntegrationsManager.registerSsaiIntegrationFactory(
    window.BrightcoveWebSdkSsaiIntegration.SsaiIntegrationFactory
  );
}

function BrightcovePlayer({
  playableItem,
  videoId,
  pluginsData,
  playNextUrl,
}: {
  playableItem: any;
  videoId?: string;
  pluginsData: any;
  appData: any;
  isNavigatingBack: boolean;
  playNextUrl: string | null;
}) {
  const [isRunningPlayNext, setIsRunningPlayNext] = useState(false);
  const [playNextFeed, setPlayNextFeed] = useState<Feed | null>(null);
  const playNextFeedRef = useRef<Feed | null>(null);
  const isRunningPlayNextRef = useRef(false);
  const closePlayNextRef = useRef(false);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  playNextFeedRef.current = playNextFeed;
  isRunningPlayNextRef.current = isRunningPlayNext;

  const { accountId, policyKey, viewerId, analyticsSharedVars } =
    useLoaderData<LoaderData>();
  const playerContainerRef = useRef<HTMLDivElement>(null);

  const [playerInstance, setPlayerInstance] = useState<Player | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (accountId) {
      // @ts-ignore - accountId exists
      const player: Player = new PlayerWithUI({ accountId });

      if (policyKey) {
        player.updateConfiguration({
          brightcove: {
            policyKey,
            viewerId,
          },
          integrations: {
            ssai: {
              timeout: 30_000,
              hideOverlays: false,
            },
          },
          playback: { autoplay: true },
        });
      }

      setPlayerInstance(player);

      return () => {
        player.dispose();
      };
    }
  }, [accountId, policyKey]);

  const loadRemoteVideo = useCallback(
    (source: { url: string; mimeType: string }) => {
      if (playerInstance) {
        playerInstance.loadRemoteVideo(source);
      }
    },
    [playerInstance]
  );

  const loadCloudVideo = useCallback(
    async (brightcoveVideoId: string) => {
      if (playerInstance) {
        const adConfigId = playableItem?.extensions?.['ad_keys'] || null;

        playerInstance.updateConfiguration({
          brightcove: {
            adConfigId,
          },
        });

        const { promise } = playerInstance.getVideoByIdFromPlaybackApi({
          videoId: brightcoveVideoId.toString(),
        });

        try {
          const brightcoveVideoModel = await promise;

          playerInstance.loadBrightcoveVideoModel(brightcoveVideoModel);
        } catch (error) {
          logger.info(`${error}`);
        }
      }
    },
    [playerInstance, playableItem]
  );

  const loadDataSources = useCallback(() => {
    const src = playableItem?.content?.src;
    const brightcoveVideoId = playableItem?.extensions?.brightcove?.video_id;

    if (brightcoveVideoId || !src) {
      loadCloudVideo(brightcoveVideoId || playableItem?.id);
    } else {
      const source = {
        url: src,
        mimeType: getVideoType(src),
      };

      loadRemoteVideo(source);
    }
  }, [loadCloudVideo, loadRemoteVideo, playableItem]);

  const streamType = useMemo(
    () => (isLive(playableItem) ? 'live' : 'vod'),
    [playableItem]
  );
  const isFreeItem =
    playableItem.extensions?.free || playableItem.extensions?.isFree;
  const analyticsCustomProperties = useMemo(
    () =>
      playableItem.extensions?.analyticsCustomProperties &&
      JSON.stringify(playableItem.extensions?.analyticsCustomProperties),
    [playableItem.extensions?.analyticsCustomProperties]
  );

  const trackData: PlayerTrackData = useMemo(
    () => ({
      ...analyticsSharedVars,
      name: playableItem?.title,
      media_id: playableItem?.id,
      media_type: playableItem.type?.value,
      duration: playableItem.extensions?.duration,
      stream_format: playableItem?.content?.type,
      stream_type: streamType,
      free_or_paid: getBoolValue(isFreeItem) ? 'free' : 'paid',
      analyticsCustomProperties,
    }),
    [
      analyticsSharedVars,
      playableItem?.title,
      playableItem?.id,
      playableItem?.content?.type,
      playableItem.extensions?.duration,
      playableItem.type?.value,
      streamType,
      isFreeItem,
      analyticsCustomProperties,
    ]
  );

  const playNext = (event: any) => {
    if (!playNextUrl || closePlayNextRef.current) return;

    const isClickEvent: boolean = event?.type === 'click';

    if (!isClickEvent) return;

    playerInstance?.detach();

    navigate(`/player?self-link=${encodeURIComponent(playNextUrl)}`, {
      replace: true,
    });
  };

  useEffect(() => {
    if (playerInstance && playerContainerRef.current) {
      playerInstance.attach(playerContainerRef.current);
      loadDataSources();
    }

    return () => {
      setIsRunningPlayNext(false);
      playNextFeedRef.current = null;
      isRunningPlayNextRef.current = false;

      if (!playerInstance) return;

      if (!document.pictureInPictureElement) playerInstance.detach();
    };
  }, [playerInstance, loadDataSources]);

  usePlayerAnalytics(playerInstance, trackData);

  usePlayerTopBar(playerInstance, playableItem, trackData);

  usePlayNext(
    playerInstance,
    playNext,
    playNextUrl,
    setIsRunningPlayNext,
    setPlayNextFeed,
    setDuration,
    setCurrentPosition
  );

  return (
    <>
      <div
        className="h-screen overflow-y-hidden overscroll-none scrollbar-hide"
        id="playerContainer"
        ref={playerContainerRef}
      />
      {isRunningPlayNext && playNextFeed && !closePlayNextRef.current && (
        <PlayNext
          playNext={playNext}
          feed={playNextFeed}
          totalDuration={duration}
          position={currentPosition}
          trackData={trackData}
          isPostroll={false}
          showPlayNextOnPostroll={true}
          closePlayNextRef={closePlayNextRef}
        />
      )}
    </>
  );
}

export default BrightcovePlayer;
